// src/pages/Inventory.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCommands } from '../api/commands';
import { useAuth } from '../auth/AuthContext';
import PageHeader from '../components/PageHeader';
import Icon from '../components/Icon';
import { GFM_SITES, GFM_SITE_KEYS, siteLabel } from '../constants/gfm';

const SITES = GFM_SITE_KEYS;

function dateToISO(dateStr){
  if (!dateStr) return undefined;
  const [y,m,d] = dateStr.split('-').map(Number);
  const dt = new Date(y, (m||1)-1, d||1, 12, 0, 0);
  return dt.toISOString();
}

/* ---------------------------------------------
 * BulkItemSearch — autocomplete search
 * --------------------------------------------- */
function BulkItemSearch({ itemNames, value, onChange }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => { if (!value) setQuery(''); }, [value]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return itemNames.slice(0, 10);
    return itemNames.filter(n => n.toLowerCase().includes(q)).slice(0, 10);
  }, [query, itemNames]);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function select(name) { setQuery(name); onChange(name); setOpen(false); }

  return (
    <div ref={ref} className="suggest">
      <input
        className="input"
        placeholder="Search items"
        value={query}
        autoComplete="off"
        onChange={e => { setQuery(e.target.value); onChange(''); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={e => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'Enter' && suggestions.length === 1) select(suggestions[0]);
        }}
      />
      {open && suggestions.length > 0 && (
        <div className="suggest-list" role="listbox">
          {suggestions.map(n => (
            <div
              key={n}
              role="option"
              aria-selected={value === n}
              className={`suggest-item${value === n ? ' selected' : ''}`}
              onMouseDown={() => select(n)}
            >
              {n}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------
 * CategoryField
 * --------------------------------------------- */
function CategoryField({ value, onChange, existingCategories }) {
  const [custom, setCustom] = useState(false);

  const known = useMemo(() => {
    return existingCategories.filter(c => c && c !== 'Uncategorised').sort((a,b) => a.localeCompare(b));
  }, [existingCategories]);

  useEffect(() => {
    if (value && !known.includes(value)) setCustom(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (custom) {
    return (
      <div style={{ display: 'flex', gap: 6 }}>
        <input className="input" placeholder="New category name" value={value} onChange={e => onChange(e.target.value)} autoFocus />
        <button type="button" className="btn secondary icon" aria-label="Pick an existing category" title="Pick an existing category" onClick={() => { setCustom(false); onChange(''); }}>
          <Icon name="x" size={15} />
        </button>
      </div>
    );
  }

  return (
    <select className="input" value={value} onChange={e => { if (e.target.value === '__new__') { setCustom(true); onChange(''); } else onChange(e.target.value); }}>
      <option value="">None</option>
      {known.map(c => <option key={c} value={c}>{c}</option>)}
      <option value="__new__">New category</option>
    </select>
  );
}

/* ---------------------------------------------
 * EditItemDialog
 * --------------------------------------------- */
function EditItemDialog({ open, initial, onCancel, onSave }) {
  const [name, setName] = useState(initial?.item || '');
  const [category, setCategory] = useState(initial?.category || '');
  const [unitPrice, setUnitPrice] = useState(Number.isFinite(Number(initial?.unitPrice)) ? Number(initial?.unitPrice) : 0);
  const [packOf, setPackOf] = useState(Number.isFinite(Number(initial?.packOf)) ? Number(initial?.packOf) : 1);

  useEffect(() => {
    if (!open) return;
    setName(initial?.item || '');
    setCategory(initial?.category || '');
    setUnitPrice(Number.isFinite(Number(initial?.unitPrice)) ? Number(initial?.unitPrice) : 0);
    setPackOf(Number.isFinite(Number(initial?.packOf)) ? Number(initial?.packOf) : 1);
  }, [open, initial]);

  if (!open) return null;
  const valid = String(name).trim().length > 0 && unitPrice >= 0 && packOf >= 1;

  return (
    <div className="modal-backdrop">
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-head">
          <h3>Edit item</h3>
        </div>
        <div className="form-grid cols-2">
          <label className="input-label span-all">Name
            <input className="input" value={name} onChange={e=>setName(e.target.value)} />
          </label>
          <label className="input-label span-all">Category
            <input className="input" value={category} onChange={e=>setCategory(e.target.value)} />
          </label>
          <label className="input-label">Unit price
            <div className="input-wrap"><span className="prefix">£</span>
              <input className="input" type="number" min="0" step="0.01"
                value={Number.isFinite(unitPrice) ? Number(unitPrice).toFixed(2) : '0.00'}
                onChange={e => { const v = Number(e.target.value); setUnitPrice(Number.isFinite(v) ? v : 0); }}
                onBlur={e => { const v = Number(e.target.value); setUnitPrice(Number.isFinite(v) ? Number(v.toFixed(2)) : 0); }}
              />
            </div>
          </label>
          <label className="input-label">Pack of
            <input className="input" type="number" min="1" step="1" value={packOf} onChange={e=>setPackOf(Math.max(1, Number(e.target.value || 1)))} />
          </label>
        </div>
        <div className="modal-foot">
          <button className="btn secondary" onClick={onCancel}>Cancel</button>
          <button className="btn" onClick={() => valid && onSave({ oldName: initial.item, data: { name: String(name).trim(), category: String(category || ''), unitPrice: Number(unitPrice || 0), packOf: Number(packOf || 1) } })} disabled={!valid}>Save</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------
 * AdjustDialog — single item withdrawal only (transfer moved to TransferDialog)
 * --------------------------------------------------------- */
function AdjustDialog({ open, itemName, teams, loading, onCancel, onConfirm }) {
  const [qty, setQty] = useState(1);
  const [team, setTeam] = useState('');
  const [dateStr, setDateStr] = useState(() => new Date().toISOString().slice(0,10));

  useEffect(() => {
    if (!open) return;
    setQty(1);
    setDateStr(new Date().toISOString().slice(0,10));
    if (!team && teams?.length) setTeam(teams[0]);
  }, [open, teams]);

  if (!open) return null;

  const confirmDisabled = !qty || qty < 1 || !dateStr || !team;

  return (
    <div className="modal-backdrop">
      <div className="modal sm" role="dialog" aria-modal="true">
        <div className="modal-head">
          <h3>Withdraw stock</h3>
          <p className="modal-sub">{itemName}</p>
        </div>
        <div className="form-grid cols-2">
          <label className="input-label">Quantity
            <input className="input" type="number" min={1} value={qty} onChange={e => setQty(Math.max(1, Number(e.target.value || 1)))} />
          </label>
          <label className="input-label">Date
            <input className="input" type="date" value={dateStr} onChange={e=>setDateStr(e.target.value)} />
          </label>
          <label className="input-label span-all">Team
            <select className="input" disabled={loading} value={team} onChange={e=>setTeam(e.target.value)}>
              {loading && <option value="">Loading</option>}
              {!loading && teams.length === 0 && <option value="">No teams</option>}
              {!loading && teams.map((t, i) => <option key={`${t}-${i}`} value={t}>{t}</option>)}
            </select>
          </label>
        </div>
        <div className="modal-foot">
          <button className="btn secondary" onClick={onCancel}>Cancel</button>
          <button className="btn" onClick={() => onConfirm({ qty, team, ts: dateToISO(dateStr) })} disabled={confirmDisabled}>Confirm withdrawal</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------
 * TransferDialog — dedicated transfer between sites
 * --------------------------------------------------------- */
function TransferDialog({ open, site, sites, rows, onCancel, onConfirm }) {
  const [destSite, setDestSite] = useState('');
  const [selName, setSelName] = useState('');
  const [qty, setQty] = useState(1);
  const [lines, setLines] = useState([]);
  const [dateStr, setDateStr] = useState(() => new Date().toISOString().slice(0,10));
  const [error, setError] = useState('');

  const otherSites = useMemo(() => (sites || []).filter(s => s !== site), [sites, site]);

  useEffect(() => {
    if (!open) return;
    setDestSite(otherSites[0] || '');
    setSelName(''); setQty(1); setLines([]);
    setDateStr(new Date().toISOString().slice(0,10));
    setError('');
  }, [open, site]);

  const itemNames = useMemo(
    () => (rows ?? []).map(r => r.item).sort((a, b) => a.localeCompare(b)),
    [rows]
  );

  if (!open) return null;

  function addLine() {
    if (!selName || Number(qty) <= 0) return;
    const currentStock = (rows || []).find(r => r.item.toLowerCase() === selName.toLowerCase());
    const available = Number(currentStock?.amount || 0);
    setLines(prev => {
      const idx = prev.findIndex(x => x.name.toLowerCase() === selName.toLowerCase());
      const newQty = idx >= 0 ? Number(prev[idx].qty) + Number(qty) : Number(qty);
      if (newQty > available) { setError(`Only ${available} of "${selName}" in stock`); return prev; }
      setError('');
      if (idx >= 0) { const next = [...prev]; next[idx] = { ...next[idx], qty: newQty }; return next; }
      return [...prev, { name: selName, qty: Number(qty), available }];
    });
    setSelName(''); setQty(1);
  }

  function removeLine(name) { setLines(prev => prev.filter(l => l.name !== name)); }
  function updateQty(name, v) {
    const val = Math.max(1, Number(v || 1));
    const currentStock = (rows || []).find(r => r.item.toLowerCase() === name.toLowerCase());
    const available = Number(currentStock?.amount || 0);
    if (val > available) { setError(`Only ${available} of "${name}" in stock`); return; }
    setError('');
    setLines(prev => prev.map(l => l.name === name ? { ...l, qty: val } : l));
  }

  const canConfirm = lines.length > 0 && destSite && dateStr;

  return (
    <div className="modal-backdrop">
      <div className="modal lg" role="dialog" aria-modal="true">
        <div className="modal-head">
          <h3>Transfer stock</h3>
          <p className="modal-sub">Move items from {siteLabel(site)} to another site.</p>
        </div>

        <div className="form-grid cols-2 mb-16">
          <label className="input-label">To site
            <select className="input" value={destSite} onChange={e => setDestSite(e.target.value)}>
              {otherSites.map(s => <option key={s} value={s}>{siteLabel(s)}</option>)}
            </select>
          </label>
          <label className="input-label">Date
            <input className="input" type="date" value={dateStr} onChange={e => setDateStr(e.target.value)} />
          </label>
        </div>

        <div className="line-builder no-price">
          <label className="input-label">Item
            <BulkItemSearch itemNames={itemNames} value={selName} onChange={setSelName} />
          </label>
          <label className="input-label">Qty
            <input className="input" type="number" min={1} value={qty} onChange={e => setQty(Math.max(1, Number(e.target.value || 1)))} />
          </label>
          <button className="btn secondary" type="button" onClick={addLine} disabled={!selName}>
            <Icon name="plus" size={15} /> Add
          </button>
        </div>

        {error && <div className="alert error">{error}</div>}

        {lines.length > 0 ? (
          <div className="table-wrap mt-16">
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num" style={{width:100}}>Available</th>
                  <th style={{width:120}}>Qty</th>
                  <th className="actions" style={{width:56}}></th>
                </tr>
              </thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l.name}>
                    <td className="cell-strong">{l.name}</td>
                    <td className="num cell-muted">{l.available}</td>
                    <td>
                      <input className="input" type="number" min={1} max={l.available} value={l.qty}
                        onChange={e => updateQty(l.name, e.target.value)} style={{ maxWidth:100 }} />
                    </td>
                    <td className="actions">
                      <button className="btn ghost small icon" aria-label={`Remove ${l.name}`} onClick={() => removeLine(l.name)}>
                        <Icon name="x" size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty">No items added yet.</div>
        )}

        <div className="modal-foot">
          <button className="btn secondary" onClick={onCancel}>Cancel</button>
          <button className="btn" onClick={() => onConfirm({ lines, destSite, ts: dateToISO(dateStr) })} disabled={!canConfirm}>
            Transfer {lines.length > 0 ? `${lines.length} item${lines.length > 1 ? 's' : ''}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------
 * BulkAdjustDialog — withdrawal only (transfer moved to TransferDialog)
 * --------------------------------------------------------- */
function BulkAdjustDialog({ open, site, rows, teams, teamsLoading, onCancel, onApplyWithdrawal }) {
  const [team, setTeam] = useState(teams?.[0] || '');
  const [dateStr, setDateStr] = useState(() => new Date().toISOString().slice(0,10));
  const [error, setError] = useState('');
  const [selName, setSelName] = useState('');
  const [qty, setQty] = useState(1);
  const [lines, setLines] = useState([]);

  useEffect(() => {
    if (!open) return;
    setTeam(teams?.[0] || '');
    setDateStr(new Date().toISOString().slice(0,10));
    setError(''); setSelName(''); setQty(1); setLines([]);
  }, [open, teams]);

  const itemNames = useMemo(
    () => (rows ?? []).map(r => r.item).sort((a, b) => a.localeCompare(b)),
    [rows]
  );

  if (!open) return null;

  function addLine() {
    if (!selName || Number(qty) <= 0) return;
    setLines(prev => {
      const idx = prev.findIndex(x => x.name.toLowerCase() === selName.toLowerCase());
      if (idx >= 0) { const next = [...prev]; next[idx] = { ...next[idx], qty: Number(next[idx].qty) + Number(qty) }; return next; }
      return [...prev, { name: selName, qty: Number(qty) }];
    });
    setSelName(''); setQty(1);
  }
  function removeLine(name) { setLines(prev => prev.filter(l => l.name !== name)); }
  function updateQty(name, v) { setLines(prev => prev.map(l => l.name === name ? { ...l, qty: Math.max(1, Number(v || 1)) } : l)); }

  async function handleApply() {
    setError('');
    if (!dateStr) { setError('Please pick a date.'); return; }
    if (!lines.length) { setError('Add at least one item.'); return; }
    try { await onApplyWithdrawal({ lines, team, ts: dateToISO(dateStr) }); }
    catch (e) { setError(e?.message || 'Bulk withdrawal failed'); }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal lg" role="dialog" aria-modal="true">
        <div className="modal-head">
          <h3>Bulk withdrawal</h3>
          <p className="modal-sub">Remove several items from {siteLabel(site)} at once.</p>
        </div>

        <div className="form-grid cols-2 mb-16">
          <label className="input-label">Team
            <select className="input" value={team} disabled={teamsLoading} onChange={e => setTeam(e.target.value)}>
              {teamsLoading && <option>Loading</option>}
              {!teamsLoading && teams.length === 0 && <option>No teams</option>}
              {!teamsLoading && teams.map((t, i) => <option key={i} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="input-label">Date
            <input className="input" type="date" value={dateStr} onChange={e => setDateStr(e.target.value)} />
          </label>
        </div>

        <div className="line-builder no-price">
          <label className="input-label">Item
            <BulkItemSearch itemNames={itemNames} value={selName} onChange={setSelName} />
          </label>
          <label className="input-label">Qty
            <input className="input" type="number" min={1} value={qty} onChange={e => setQty(Math.max(1, Number(e.target.value || 1)))} />
          </label>
          <button className="btn secondary" type="button" onClick={addLine} disabled={!selName}>
            <Icon name="plus" size={15} /> Add
          </button>
        </div>

        {lines.length > 0 ? (
          <div className="table-wrap mt-16" style={{ maxHeight: 280, overflowY: 'auto' }}>
            <table className="table">
              <thead><tr><th>Item</th><th style={{width:120}}>Qty</th><th className="actions" style={{width:56}}></th></tr></thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l.name}>
                    <td className="cell-strong">{l.name}</td>
                    <td><input className="input" type="number" min={1} value={l.qty} onChange={e => updateQty(l.name, e.target.value)} style={{maxWidth:100}} /></td>
                    <td className="actions">
                      <button className="btn ghost small icon" aria-label={`Remove ${l.name}`} onClick={() => removeLine(l.name)}>
                        <Icon name="x" size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty">No items added yet.</div>
        )}

        {error && <div className="alert error">{error}</div>}

        <div className="modal-foot">
          <button className="btn secondary" type="button" onClick={onCancel}>Close</button>
          <button className="btn" type="button" onClick={handleApply} disabled={!lines.length}>Apply withdrawal</button>
        </div>
      </div>
    </div>
  );
}

function SiteTabs({ site }) {
  return (
    <nav className="tabs" aria-label="Sites">
      {GFM_SITES.map(s => (
        <Link key={s.key} to={`/inventory/${s.key}`} className={`tab${s.key === site ? ' active' : ''}`}>
          {s.label}
        </Link>
      ))}
    </nav>
  );
}

function Row({ row, onAdjust, onRemove, onEdit, stockMode, counted, onCountedChange, onOrder, canEdit }) {
  const expected = Number(row.amount || 0);
  const c = Number(counted ?? expected);
  const diff = c - expected;

  return (
    <tr>
      <td className="cell-strong" style={canEdit ? { cursor:'pointer' } : undefined} onClick={()=>onEdit(row)}>
        {row.item}
        {!!row.packOf && Number(row.packOf) >= 1 && Number(row.packOf) !== 1 && (
          <span className="badge pack">×{Number(row.packOf)} pack</span>
        )}
      </td>
      <td style={{width: stockMode ? 260 : 170}}>
        {stockMode ? (
          <div className="count-cell">
            <span className="expected">Expected {expected.toLocaleString('en-GB')}</span>
            <input className="input" type="number" min={0} value={c} aria-label={`Counted ${row.item}`} onChange={e => onCountedChange(row.item, e.target.value)} />
          </div>
        ) : (
          <div className="qty-stepper">
            <button type="button" onClick={()=>onAdjust(row.item)} title="Withdraw" aria-label={`Withdraw ${row.item}`}>
              <Icon name="minus" size={15} />
            </button>
            <span className={`qty-value${expected === 0 ? ' zero' : ''}`}>{expected.toLocaleString('en-GB')}</span>
            <button type="button" className="plus" onClick={()=>onOrder(row.item)} title="Order more" aria-label={`Order ${row.item}`}>
              <Icon name="plus" size={15} />
            </button>
          </div>
        )}
      </td>
      {stockMode && (
        <td>
          <span className={`diff ${diff > 0 ? 'up' : diff < 0 ? 'down' : 'zero'}`}>
            {diff > 0 ? `+${diff}` : diff}
          </span>
        </td>
      )}
      <td className="cell-muted">{row.category || '—'}</td>
      <td>
        £{Number(row.unitPrice || 0).toFixed(2)}
        {Number(row.packOf || 1) > 1 && (
          <div className="per-unit">£{(Number(row.unitPrice || 0) / Number(row.packOf || 1)).toFixed(2)} per unit</div>
        )}
      </td>
      {canEdit && (
        <td className="actions">
          {!stockMode ? (
            <div className="row-actions">
              <button className="btn secondary small" onClick={()=>onEdit(row)}>Edit</button>
              <button className="btn secondary small destructive" onClick={()=>onRemove(row.item)}>Remove</button>
            </div>
          ) : (
            <span className="cell-muted">—</span>
          )}
        </td>
      )}
    </tr>
  );
}

export default function Inventory(){
  const cmd = useCommands();
  const params = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canEdit = useMemo(() => {
    const perms = Array.isArray(user?.scopes) ? user.scopes.map(x => String(x).toLowerCase()) : [];
    return perms.includes('editallow');
  }, [user]);

  const site = useMemo(() => {
    const s = (params.site || '').toLowerCase();
    return SITES.includes(s) ? s : 'main';
  }, [params.site]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ item:'', amount:0, category:'', unitPrice:0, packOf:1 });
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustItem, setAdjustItem] = useState('');
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [lastTeam, setLastTeam] = useState('');

  const [stockMode, setStockMode] = useState(false);
  const [counts, setCounts] = useState({});
  const [newFound, setNewFound] = useState({ item:'', qty:0, category:'', unitPrice:0, packOf:1 });
  const [foundOpen, setFoundOpen] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editInitial, setEditInitial] = useState(null);

  const existingCategories = useMemo(() => Array.from(new Set(rows.map(r => r.category).filter(Boolean))), [rows]);

  function startStockCheck(){
    const seed = {}; for (const r of rows) seed[r.item] = Number(r.amount || 0);
    setCounts(seed); setNewFound({ item:'', qty:0, category:'', unitPrice:0, packOf:1 }); setStockMode(true);
  }
  function cancelStockCheck(){ setStockMode(false); setCounts({}); setNewFound({ item:'', qty:0, category:'', unitPrice:0, packOf:1 }); setFoundOpen(false); }
  function setAllCount(val){ const v = Math.max(0, Number(val || 0)); const next = {}; for (const r of rows) next[r.item] = v; setCounts(next); }
  function updateCount(name, val){ setCounts(c => ({ ...c, [name]: Math.max(0, Number(val || 0)) })); }

  async function applyStockCheck(){
    const ops = [];
    for (const r of rows){
      const expected = Number(r.amount || 0);
      const counted  = Number(counts[r.item] ?? expected);
      const delta = counted - expected;
      if (delta > 0) ops.push({ type:'inc', id:r.item, delta });
      else if (delta < 0) ops.push({ type:'dec', id:r.item, delta: Math.abs(delta) });
    }
    if (newFound.item && Number(newFound.qty) > 0){
      ops.push({ type:'add', item: { id:newFound.item, name:newFound.item, qty:Number(newFound.qty), category:newFound.category||'', unitPrice:Number(newFound.unitPrice||0), ...(Number(newFound.packOf||1) >= 1 ? { packOf:Number(newFound.packOf) } : {}) } });
    }
    if (!ops.length){ setError('No differences to apply.'); return; }
    try { await cmd.inventory.patch(site, { ops, reason: `Stock Check ${new Date().toISOString().slice(0,10)}`, team: 'Stock Check' }); await load(); cancelStockCheck(); }
    catch (e){ setError(e?.response?.data?.error || e?.message || 'Failed to apply stock check'); }
  }

  async function load(){
    setLoading(true); setError('');
    try { const data = await cmd.inventory.get(site); setRows(Array.isArray(data) ? data : []); }
    catch (e){ setError(e?.response?.data?.error || e?.message || 'Failed to load inventory'); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [site]);

  async function loadTeamsIfNeeded(){
    if (teams.length || teamsLoading) return;
    setTeamsLoading(true);
    try {
      const list = cmd.gfmTeams?.list ? await cmd.gfmTeams.list() : await cmd.teams.list();
      const names = (Array.isArray(list) ? list : []).map(x => (typeof x === 'string' ? x : (x?.name || x?.id || ''))).filter(Boolean);
      setTeams(Array.from(new Set(names)));
    } catch (e){ console.error('Failed to load teams', e); setTeams([]); }
    finally { setTeamsLoading(false); }
  }

  async function tryPatch(ops, reason, team, ts){
    try { await cmd.inventory.patch(site, { ops, reason, team, ts }); return true; } catch { return false; }
  }

  async function applyChange(applyFn, reason, team, ts){
    setError('');
    const current = [...rows];
    const next = applyFn(current);
    if (next._error) { setError(next._error); return; }
    const patched = await tryPatch(next._ops || [], reason, team, ts);
    if (!patched){
      try { await cmd.inventory.replace(site, next); }
      catch (e){ setError(e?.response?.data?.error || e?.message || 'Update failed'); return; }
    }
    await load();
  }

  function incOpsByName(name, delta){
    return (list) => {
      const idx = list.findIndex(x => x.item === name);
      if (idx === -1){
        const item = { item: name, amount: Math.max(delta, 0), category:'', unitPrice:0, packOf:1 };
        const next = [...list, item];
        next._ops = [{ type:'add', item: { id:name, name, qty:item.amount, category:item.category, unitPrice:item.unitPrice, ...(Number(item.packOf||1) >= 1 ? { packOf:Number(item.packOf) } : {}) } }];
        return next;
      }
      const updated = { ...list[idx], amount: Number(list[idx].amount || 0) + delta };
      const next = [...list]; next[idx] = updated;
      next._ops = [{ type: delta >= 0 ? 'inc' : 'dec', id: name, delta: Math.abs(delta) }];
      return next;
    };
  }

  const categories = useMemo(() => {
    const unique = Array.from(new Set(rows.map(r => r.category || 'Uncategorised')));
    return ['all', ...unique.sort((a,b) => a.localeCompare(b))];
  }, [rows]);

  function removeByName(name){ return (list) => { const next = list.filter(x => x.item !== name); next._ops = [{ type:'remove', id: name }]; return next; }; }

  function addItemLocally(newItemInput){
    return (list) => {
      const exists = list.find(x => x.item === newItemInput.item);
      const baseData = { name:newItemInput.item, qty:newItemInput.amount, category:newItemInput.category, unitPrice:newItemInput.unitPrice, ...(Number(newItemInput.packOf||1) >= 1 ? { packOf:Number(newItemInput.packOf) } : {}) };
      let next;
      if (exists){ next = list.map(x => x.item === newItemInput.item ? { ...exists, ...newItemInput } : x); next._ops = [{ type:'set', id:newItemInput.item, data: baseData }]; }
      else { next = [...list, newItemInput]; next._ops = [{ type:'add', item: { id:newItemInput.item, ...baseData } }]; }
      return next;
    };
  }

  function updateItemLocally(oldName, data){
    return (list) => {
      const idx = list.findIndex(x => x.item === oldName);
      if (idx === -1) { const next = [...list]; next._ops = []; return next; }
      const newName = data.name.trim();
      if (newName !== oldName && list.some(x => x.item.toLowerCase() === newName.toLowerCase())) {
        const next = [...list]; next._ops = []; next._error = `Item called "${newName}" already exists`; return next;
      }
      const merged = { ...list[idx], item: newName, category: data.category || '', unitPrice: Number(data.unitPrice || 0), packOf: Number(data.packOf || 1) };
      const next = [...list]; next[idx] = merged;
      next._ops = [{ type:'set', id: oldName, data: { name: newName, category: merged.category, unitPrice: merged.unitPrice, ...(Number(merged.packOf||1) >= 1 ? { packOf:Number(merged.packOf) } : {}) } }];
      return next;
    };
  }

  const onRemove = (name) => applyChange(removeByName(name), 'Remove item', lastTeam || undefined);

  const onAdjustOpen = async (name) => {
    setAdjustItem(name); setAdjustOpen(true); await loadTeamsIfNeeded();
  };

  const onAdjustConfirm = async ({ qty, team, ts }) => {
    setLastTeam(team);
    await applyChange(incOpsByName(adjustItem, -qty), `Withdrawal (${team})`, team, ts);
    setAdjustOpen(false);
  };

  async function onAdd(e){
    e.preventDefault();
    if (!newItem.item) return setError('Item name is required');
    const payload = { item: newItem.item, amount: Number(newItem.amount || 0), category: newItem.category || '', unitPrice: Number(newItem.unitPrice || 0), packOf: Math.max(1, Number(newItem.packOf || 1)) };
    await applyChange(addItemLocally(payload), 'Add item', lastTeam || undefined);
    setAdding(false); setNewItem({ item:'', amount:0, category:'', unitPrice:0, packOf:1 });
  }

  function openEdit(row){ setEditInitial(row); setEditOpen(true); }

  async function saveEdit({ oldName, data }){
    if (data.name.trim() !== oldName && rows.some(r => r.item.toLowerCase() === data.name.trim().toLowerCase())){
      setError(`Item called "${data.name.trim()}" already exists`); return;
    }
    await applyChange(updateItemLocally(oldName, data), 'Edit item details', lastTeam || undefined);
    setEditOpen(false); setEditInitial(null);
  }

  const filtered = useMemo(() => rows.filter(r => {
    const matchesCategory = filterCategory === 'all' || (r.category || 'Uncategorised') === filterCategory;
    const matchesSearch = search.trim() === '' || (r.item || '').toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  }), [rows, filterCategory, search]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const catA = (a.category || '').toLowerCase(), catB = (b.category || '').toLowerCase();
    if (catA < catB) return -1; if (catA > catB) return 1;
    return (a.item || '').localeCompare(b.item || '');
  }), [filtered]);

  const differing = useMemo(() => {
    if (!stockMode) return 0;
    return rows.filter(r => Number(counts[r.item] ?? r.amount ?? 0) !== Number(r.amount || 0)).length;
  }, [stockMode, rows, counts]);

  function buildMap(){ return Object.fromEntries((rows||[]).map(r => [String(r.item).toLowerCase(), r])); }

  async function handleBulkWithdrawal({ lines, team, ts }){
    const itemsMap = buildMap();
    const ops = [];
    for (const {name, qty} of lines){
      const it = itemsMap[name.toLowerCase()];
      if (!it) throw new Error(`Item not found for withdrawal: "${name}"`);
      ops.push({ type:'dec', id: it.item, delta: Number(qty) });
    }
    await cmd.inventory.patch(site, { ops, reason: 'Bulk withdrawal', team, ts });
    await load(); setBulkOpen(false);
  }

  async function handleTransfer({ lines, destSite, ts }){
    const itemsMap = buildMap();
    const decOps = [], incOps = [];
    for (const {name, qty} of lines){
      const it = itemsMap[name.toLowerCase()];
      if (!it) throw new Error(`Item not found: "${name}"`);
      decOps.push({ type:'dec', id: it.item, delta: Number(qty) });
      incOps.push({ type:'inc', id: it.item, delta: Number(qty) });
    }
    await cmd.inventory.patch(site, { ops: decOps, reason: `Transfer to ${destSite}`, team: site, ts });
    await cmd.inventory.patch(destSite, { ops: incOps, reason: `Transfer from ${site}`, team: destSite, ts });
    await load(); setTransferOpen(false);
  }

  const goOrderItem = (itemName) => nav(`/gfm/deliveries/new?site=${site}&item=${encodeURIComponent(itemName)}`);

  return (
    <div className="container">
      <PageHeader
        eyebrow="GFM inventory"
        title={siteLabel(site)}
        actions={!stockMode && (
          <>
            <button className="btn secondary" onClick={startStockCheck}>Stock check</button>
            <button className="btn secondary" onClick={() => setTransferOpen(true)}>Transfer stock</button>
            <button className="btn secondary" onClick={async () => { await loadTeamsIfNeeded(); setBulkOpen(true); }}>Bulk withdrawal</button>
            <button className="btn secondary" onClick={()=>setAdding(v=>!v)}>{adding ? 'Cancel' : 'Add item'}</button>
            <button className="btn" onClick={() => nav(`/gfm/deliveries/new?site=${site}`)}>
              <Icon name="plus" size={15} /> New order
            </button>
          </>
        )}
      />

      {!stockMode && <SiteTabs site={site} />}

      {error && <div className="page-alerts"><div className="alert error">{error}</div></div>}

      {stockMode && (
        <div className="stock-banner">
          <div className="stock-banner-text">
            <strong>Stock check: {siteLabel(site)}</strong>
            {differing === 0 ? 'No differences yet' : `${differing} item${differing === 1 ? ' differs' : 's differ'} from expected`}
          </div>
          <div className="stock-tools">
            <button className="btn secondary small" onClick={()=>setAllCount(0)}>Set all to 0</button>
            <button className="btn secondary small" onClick={()=>startStockCheck()}>Reset counts</button>
            <button className="btn secondary small" onClick={()=>setFoundOpen(v=>!v)} aria-expanded={foundOpen}>
              <Icon name={foundOpen ? 'minus' : 'plus'} size={14} /> Found new item
            </button>
            <button className="btn secondary small stock-cancel" onClick={cancelStockCheck}>Cancel check</button>
            <button className="btn small stock-apply" onClick={applyStockCheck}>Apply differences</button>
          </div>
        </div>
      )}

      {adding && (
        <div className="card mb-16">
          <div className="card-head"><h2 className="card-title">Add item</h2></div>
          <form className="add-form" onSubmit={onAdd}>
            <div className="field">
              <label className="input-label">Item name <span className="req">*</span></label>
              <input className="input" placeholder="e.g. 13W 9Pin Bulbs" value={newItem.item} onChange={e => setNewItem(v => ({ ...v, item: e.target.value }))} autoFocus />
            </div>
            <div className="field small">
              <label className="input-label">Amount <span className="req">*</span></label>
              <input className="input" type="number" min={0} step="1" value={newItem.amount} onChange={e => setNewItem(v => ({ ...v, amount: e.target.value }))} />
            </div>
            <div className="field">
              <label className="input-label">Category</label>
              <CategoryField value={newItem.category} onChange={val => setNewItem(v => ({ ...v, category: val }))} existingCategories={existingCategories} />
            </div>
            <div className="field small">
              <label className="input-label">Item or pack price</label>
              <div className="input-wrap"><span className="prefix">£</span>
                <input className="input" type="number" min="0" step="0.01" placeholder="0.00" value={newItem.unitPrice} onChange={e => setNewItem(v => ({ ...v, unitPrice: e.target.value }))} />
              </div>
            </div>
            <div className="field small">
              <label className="input-label">Pack of</label>
              <input className="input" type="number" min="1" step="1" value={newItem.packOf} onChange={e => setNewItem(v => ({ ...v, packOf: Math.max(1, Number(e.target.value || 1)) }))} />
            </div>
            <div className="meta">
              {(() => {
                const qty   = Math.max(0, Number(newItem.amount || 0));
                const price = Math.max(0, Number(newItem.unitPrice || 0));
                const total = qty * price;
                const exists = (rows || []).some(r => String(r.item || '').toLowerCase().trim() === String(newItem.item || '').toLowerCase().trim());
                return (
                  <>
                    <div className="preview">
                      <span className={`badge ${newItem.item && qty > 0 ? 'ok' : 'warn'}`}>{newItem.item && qty > 0 ? 'Ready to add' : 'Enter name and amount'}</span>
                      <span className="muted">{qty || 0}{price ? ` × £${price.toFixed(2)} = £${total.toFixed(2)}` : ''}</span>
                      {exists && <span className="badge err">Name already exists</span>}
                    </div>
                    <div className="actions">
                      <button className="btn secondary" type="button" onClick={() => setAdding(false)}>Close</button>
                      <button className="btn" type="submit" disabled={!newItem.item || Number(newItem.amount || 0) <= 0}>Save item</button>
                    </div>
                  </>
                );
              })()}
            </div>
          </form>
        </div>
      )}

      {stockMode && foundOpen && (
        <div className="card inset found-card">
          <div className="card-head" style={{marginBottom:12}}>
            <h2 className="card-title">Found a new item</h2>
          </div>
          <div className="found-grid">
            <label className="input-label">Item name <span className="req">*</span>
              <input className="input" placeholder="e.g. 13W 9Pin Bulbs" value={newFound.item}
                onChange={e=>setNewFound(v=>({ ...v, item:e.target.value }))} />
            </label>
            <label className="input-label">Quantity <span className="req">*</span>
              <input className="input" type="number" min={0} value={newFound.qty}
                onChange={e=>setNewFound(v=>({ ...v, qty: Number(e.target.value || 0) }))} />
            </label>
            <label className="input-label">Category
              <input className="input" placeholder="Optional" value={newFound.category}
                onChange={e=>setNewFound(v=>({ ...v, category: e.target.value }))} />
            </label>
            <label className="input-label">Unit price
              <div className="input-wrap"><span className="prefix">£</span>
                <input className="input" type="number" step="0.01" min={0} placeholder="0.00" value={newFound.unitPrice}
                  onChange={e=>setNewFound(v=>({ ...v, unitPrice: e.target.value }))} />
              </div>
            </label>
            <label className="input-label">Pack of
              <input className="input" type="number" min={1} step={1} value={newFound.packOf}
                onChange={e=>setNewFound(v=>({ ...v, packOf: Math.max(1, Number(e.target.value || 1)) }))} />
            </label>
            <div className="found-preview">
              {(() => {
                const qty = Math.max(0, Number(newFound.qty || 0));
                const price = Math.max(0, Number(newFound.unitPrice || 0));
                const ready = String(newFound.item || '').trim() && qty > 0;
                return (
                  <>
                    <span className={`badge ${ready ? 'ok' : 'warn'}`}>
                      {ready ? 'Will be added when you apply' : 'Enter name and quantity'}
                    </span>
                    {String(newFound.item || '').trim() && (
                      <div className="found-line">
                        <span className="found-name">{newFound.item}</span>
                        <span>{qty.toLocaleString('en-GB')}{price ? ` × £${price.toFixed(2)} = £${(qty * price).toFixed(2)}` : ''}</span>
                      </div>
                    )}
                    <button type="button" className="ghost" onClick={()=>setNewFound({ item:'', qty:0, category:'', unitPrice:0, packOf:1 })}>
                      Clear
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="toolbar mb-16">
          <input className="input grow" placeholder="Search items" value={search} onChange={e=>setSearch(e.target.value)} />
          <select className="input" value={filterCategory} onChange={e=>setFilterCategory(e.target.value)}>
            {categories.map(c => <option key={c} value={c}>{c === 'all' ? 'All categories' : c}</option>)}
          </select>
          {(search || filterCategory !== 'all') && (
            <button className="btn ghost" onClick={()=>{setSearch(''); setFilterCategory('all');}}>Clear filters</button>
          )}
          <span className="spacer" />
          {!loading && <span className="muted">{sorted.length === rows.length ? `${rows.length} items` : `${sorted.length} of ${rows.length} items`}</span>}
        </div>

        {loading ? <div className="empty">Loading</div> : sorted.length === 0 ? (
          <div className="empty">{rows.length === 0 ? 'No items in this inventory.' : 'No items match your filters.'}</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>{stockMode ? 'Expected and counted' : 'Amount'}</th>
                  {stockMode && <th>Difference</th>}
                  <th>Category</th>
                  <th>Unit price</th>
                  {canEdit && <th className="actions">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map(it => (
                  <Row
                    key={it.item}
                    row={it}
                    onAdjust={onAdjustOpen}
                    onRemove={onRemove}
                    onEdit={openEdit}
                    stockMode={stockMode}
                    counted={counts[it.item]}
                    onCountedChange={updateCount}
                    onOrder={goOrderItem}
                    canEdit={canEdit}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AdjustDialog
        open={adjustOpen}
        itemName={adjustItem}
        teams={teams}
        loading={teamsLoading}
        onCancel={()=>setAdjustOpen(false)}
        onConfirm={onAdjustConfirm}
      />

      <TransferDialog
        open={transferOpen}
        site={site}
        sites={SITES}
        rows={rows}
        onCancel={()=>setTransferOpen(false)}
        onConfirm={handleTransfer}
      />

      {stockMode && (
        <div className="stockbar">
          <button className="btn secondary" onClick={cancelStockCheck}>Cancel</button>
          <button className="btn" onClick={applyStockCheck}>Apply</button>
        </div>
      )}

      <BulkAdjustDialog
        open={bulkOpen}
        site={site}
        rows={rows}
        teams={teams}
        teamsLoading={teamsLoading}
        onCancel={()=>setBulkOpen(false)}
        onApplyWithdrawal={handleBulkWithdrawal}
      />

      {canEdit && (
        <EditItemDialog
          open={editOpen}
          initial={editInitial}
          onCancel={()=>{ setEditOpen(false); setEditInitial(null); }}
          onSave={saveEdit}
        />
      )}
    </div>
  );
}
